import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

import { getTenantContext } from "../services/tenant";
import SessionUnavailable from "../components/SessionUnavailable";
import FullPageLoader from "../components/BrandedLoader";
import DomainNotConfigured from "@/pages/DomainNotConfigured";
import MerchantNotFound from "@/pages/MerchantNotFound";
import { applyBrandColor } from "@/lib/color";
import { MERCHANT_CODE } from "@/lib/tenant";
import { setFavicon } from "../lib/favicon";
import { useLoaderBranding } from "./LoaderBrandingContext";
import useLiveBranding from "../hooks/useLiveBranding";
import { tenantBrandingScope } from "../lib/brandingChanges";

const TenantContext = createContext({
  modules: {},
  features: {},
  branding: {},
  merchant: null,
  loading: true,
});

/**
 * Resolves current merchant context and applies tenant branding
 * before rendering the tenant application.
 */
export function TenantProvider({ children }) {
  const location = useLocation();
  const isMaster = location.pathname.startsWith("/master");

  const preview = new URLSearchParams(
    location.search
  ).get("merchant");

  const tenantKey =
    preview ||
    MERCHANT_CODE ||
    window.location.hostname;

  const cacheKey = `tenant_branding_${tenantKey}`;
  const startupBrand = useMemo(() => {
    if (isMaster) return null;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (!cached || typeof cached !== "object" || Array.isArray(cached)) return null;
      const name = typeof cached.company_name === "string" && cached.company_name.trim()
        ? cached.company_name : typeof cached.page_title === "string" ? cached.page_title : "";
      const logoUrl = typeof cached.website_logo_url === "string" && cached.website_logo_url
        ? cached.website_logo_url : typeof cached.logo_url === "string" ? cached.logo_url : "";
      return name ? { scope: `tenant:${cacheKey}`, name, logoUrl, merchantCode: preview || MERCHANT_CODE || "" } : null;
    } catch { return null; }
  }, [cacheKey, isMaster, preview]);

  const [ctx, setCtx] = useState({
    modules: {},
    features: {},
    branding: {},
    merchant: null,
    loading: true,
  });

  const [blocked, setBlocked] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const loaderBrandReady = !isMaster && !ctx.loading && ctx.resolvedKey === cacheKey && !blocked && !error;
  useLoaderBranding({ scope: `tenant:${cacheKey}`,
    name: loaderBrandReady ? ctx.loader_branding?.company_name || ctx.branding?.company_name || ctx.merchant?.name || "" : "",
    logoUrl: loaderBrandReady ? ctx.loader_branding?.logo_url ?? (ctx.branding?.website_logo_url || ctx.branding?.logo_url || "") : "",
    merchantCode: loaderBrandReady ? ctx.merchant?.code_url || ctx.merchant?.url_code || MERCHANT_CODE || preview || "" : "" });

  /**
   * Apply complete tenant branding
   */
  const applyBranding = useCallback((branding = {}, merchantCode = MERCHANT_CODE || preview || "") => {
    if (isMaster) return;

    // Primary brand color
    if (branding.primary_color) {
      applyBrandColor(branding.primary_color);
    }

    // Page title
    if (branding.page_title) {
      document.title = branding.page_title;
    }

    // Also reset an absent icon, so another merchant's icon cannot linger.
    setFavicon(branding.favicon_url, { merchantCode });
  }, [isMaster, preview]);

  const loadBranding = useCallback(signal => getTenantContext(preview, signal), [preview]);
  const merchantId = ctx.merchant?.id;
  const refreshBranding = useLiveBranding(loaderBrandReady ? tenantBrandingScope(merchantId) : "", loadBranding, data => {
    // A branding check cannot change identity, permissions, modules or page state.
    if (!data?.merchant?.id || data.merchant.id !== merchantId ||
      (data.branding?.merchant_id && data.branding.merchant_id !== merchantId)) return;
    const branding = data.branding || {};
    if (typeof branding !== "object" || Array.isArray(branding)) return;
    if (JSON.stringify(ctx.branding) !== JSON.stringify(branding)) {
      applyBranding(branding, ctx.merchant?.code_url || ctx.merchant?.url_code || MERCHANT_CODE || preview || "");
      try { localStorage.setItem(cacheKey, JSON.stringify(branding)); } catch { /* Keep in-memory branding when storage is blocked. */ }
    }
    setCtx(previous => {
      if (previous.resolvedKey !== cacheKey || previous.merchant?.id !== merchantId) return previous;
      const name = typeof data.merchant.name === "string" ? data.merchant.name : previous.merchant.name;
      if (JSON.stringify(previous.branding) === JSON.stringify(branding) &&
        JSON.stringify(previous.loader_branding) === JSON.stringify(data.loader_branding) && name === previous.merchant.name) return previous;
      return { ...previous, branding, loader_branding: data.loader_branding,
        merchant: name === previous.merchant.name ? previous.merchant : { ...previous.merchant, name } };
    });
  });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setBlocked(null);
    setError("");
    setCtx({ modules: {}, features: {}, branding: {}, merchant: null, loading: true });

    const loadTenant = async () => {
      /*
       * --------------------------------------------------
       * STEP 1:
       * Apply cached branding immediately if available.
       *
       * This prevents:
       * orange -> merchant color
       * on repeat visits.
       * --------------------------------------------------
       */
      if (!isMaster) {
        applyBranding({});
        try {
          const cachedBranding =
            localStorage.getItem(cacheKey);

          if (cachedBranding) {
            const parsed = JSON.parse(cachedBranding);

            applyBranding(parsed);
          }
        } catch (error) {
          console.warn(
            "Unable to load cached tenant branding:",
            error
          );
        }
      }

      try {
        /*
         * --------------------------------------------------
         * STEP 2:
         * Get latest tenant information from DB/API.
         * --------------------------------------------------
         */
        const data = await getTenantContext(preview, controller.signal);

        if (!active) return;

        const branding = data.branding || {};

        /*
         * IMPORTANT:
         * Apply branding BEFORE rendering application.
         */
        applyBranding(branding, data.merchant?.code_url || data.merchant?.url_code || MERCHANT_CODE || preview || "");

        /*
         * Cache branding for next page load.
         */
        if (!isMaster && Object.keys(branding).length) {
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify(branding)
            );
          } catch (error) {
            console.warn(
              "Unable to cache tenant branding:",
              error
            );
          }
        }

        /*
         * Render application only AFTER branding
         * has already been applied.
         */
        setCtx({
          ...data,
          branding,
          loading: false,
          resolvedKey: cacheKey,
        });
      } catch (error) {
        if (!active) return;

        const detail =
          error?.response?.data?.detail;

        const code = detail?.code || error?.response?.data?.code;
        const info = typeof detail === "object" && detail ? detail : error?.response?.data || {};

        if (code === "DOMAIN_NOT_CONFIGURED") {
          setBlocked({
            kind: "domain",
            value:
              info.domain ||
              window.location.hostname,
          });
        } else if (code === "MERCHANT_NOT_FOUND") {
          setBlocked({
            kind: "merchant",
            value:
              info.merchant_code ||
              preview ||
              MERCHANT_CODE,
          });
        } else setError("Unable to load this merchant’s configuration. Please retry.");

        setCtx((current) => ({
          ...current,
          loading: false,
          resolvedKey: cacheKey,
        }));
      }
    };

    loadTenant();

    return () => {
      active = false;
      controller.abort();
    };
  }, [applyBranding, cacheKey, isMaster, preview, attempt]);

  /*
   * Error screens
   */
  if (blocked?.kind === "domain") {
    return (
      <DomainNotConfigured
        domain={blocked.value}
      />
    );
  }

  if (blocked?.kind === "merchant") {
    return (
      <MerchantNotFound
        code={blocked.value}
      />
    );
  }

  /*
   * IMPORTANT:
   *
   * Do NOT render tenant UI before branding
   * has been resolved.
   *
   * Reuse the request-loader design with only this route's cached public brand.
   * Cached presentation never grants access to the tenant application.
   */
  if ((ctx.loading || ctx.resolvedKey !== cacheKey) && !isMaster) {
    return <FullPageLoader brand={startupBrand} label="Loading your workspace…" hint="Preparing your merchant configuration." />;
  }

  if (error) return <SessionUnavailable message={error} onRetry={() => setAttempt(value => value + 1)} />;

  return (
    <TenantContext.Provider value={{ ...ctx, refreshBranding }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () =>
  useContext(TenantContext);

export { moduleEnabled, moduleReadOnly, featureEnabled } from "@/lib/moduleAccess";
